import {Popover} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useRef, useState} from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import * as Keyboard from 'util/Keyboard';
import * as logging from 'util/Logging';

export enum FeedbackReaction {
  THUMBS_UP = 'THUMBS_UP',
  THUMBS_DOWN = 'THUMBS_DOWN',
}

const faThumbClassName = {
  [FeedbackReaction.THUMBS_UP]: 'fa fa-thumbs-up',
  [FeedbackReaction.THUMBS_DOWN]: 'fa fa-thumbs-down',
};

const SubmittedReactionGlobalState = makeUseGlobalClientState<FeedbackReaction | null>(null);

export interface ExperimentOptimizationFeedbackToolbarProps {
  clientStateId: string;
  feedbackContext: Record<string, unknown>;
}

export default function ExperimentOptimizationFeedbackToolbar({
  clientStateId,
  feedbackContext,
}: ExperimentOptimizationFeedbackToolbarProps): JSX.Element {
  const [submittedReaction, setSubmittedReaction] =
    SubmittedReactionGlobalState.useGlobalClientState(clientStateId);
  const [pendingReaction, setPendingReaction] = useState<FeedbackReaction | null>(null);
  const [pendingTextFeedback, setPendingTextFeedback] = useState<string | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  const clearPendingState = useCallback(() => {
    setPendingTextFeedback('');
    setPendingReaction(null);
  }, []);

  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (pendingReaction) {
      logging.info('Experiment optimization feedback', {
        extra: {reaction: pendingReaction, textFeedback: pendingTextFeedback, ...feedbackContext},
      });
      setSubmittedReaction(pendingReaction);
      clearPendingState();
    }
    setIsPopoverOpen(false);
  };

  const handleFormKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
    // Support common UX of letting user submit form when pressing Ctrl+Enter in text area.
    if (Keyboard.isOSCtrlKey(e) && Keyboard.isEnterKey(e)) {
      e.preventDefault();
      formRef.current!.requestSubmit();
    }
  }, []);

  return (
    <Popover
      opened={isPopoverOpen}
      onChange={setIsPopoverOpen}
      closeOnClickOutside={false}
      withArrow={true}
      withinPortal={true}
      arrowSize={20}
      shadow="md"
      width={650}
    >
      <Popover.Target>
        <div className={css(styles.toolbar)}>
          {[FeedbackReaction.THUMBS_UP, FeedbackReaction.THUMBS_DOWN].map((reaction) => (
            <Button
              key={reaction}
              buttonStyle={
                pendingReaction === reaction || submittedReaction === reaction
                  ? ButtonStyle.TERTIARY_BLUE_POWDER
                  : ButtonStyle.TERTIARY_WHITE
              }
              disabled={submittedReaction != null}
              size={ButtonSize.SMALL}
              onClick={() => {
                if (submittedReaction != null) {
                  return;
                }
                setPendingReaction(reaction);
                setIsPopoverOpen(true);
              }}
            >
              <span className={faThumbClassName[reaction]} role="button" aria-label={reaction} />
            </Button>
          ))}
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <form ref={formRef} onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
          <div className={css(styles.formContents)}>
            <div>
              <label htmlFor="feedback">
                {pendingReaction === FeedbackReaction.THUMBS_UP
                  ? 'Help us improve by sharing more about your experience.'
                  : 'Let us know what went wrong or how we can improve.'}
              </label>
              <textarea
                value={pendingTextFeedback ?? ''}
                onChange={(e) => {
                  setPendingTextFeedback(e.target.value);
                }}
                name="feedback"
                className={css(styles.textarea)}
                rows={6}
              />
            </div>
          </div>
          <div className={css(styles.buttonContainer)}>
            <Button
              text="Cancel"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MINI}
              onClick={() => {
                clearPendingState();
                setIsPopoverOpen(false);
              }}
            />
            <Button text="Submit" type="submit" buttonStyle={ButtonStyle.SECONDARY} size={ButtonSize.MINI} />
          </div>
        </form>
      </Popover.Dropdown>
    </Popover>
  );
}

const styles = StyleSheet.create({
  toolbar: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  formContents: {
    display: 'grid',
    gap: 12,
    fontSize: 14,
  },
  textarea: {
    width: '100%',
    minHeight: '120px',
    padding: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    border: '1px solid #ccc',
    borderRadius: '4px',
    resize: 'vertical',
  },
  buttonContainer: {
    marginTop: '8px',
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
});
